
import React, { useState } from 'react';
import { 
  LayoutDashboard, Users, MapPin, Coffee, Calendar, Settings, LogOut, 
  MessageSquare, Trophy, Moon, Sun, ShoppingCart, Utensils, BarChart2, 
  HelpCircle, CreditCard, Shield, ChevronDown, ChevronRight, Heart, Briefcase, Headphones
} from 'lucide-react';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  badge?: number;
}

interface MenuGroup {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  items: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, setActiveTab, theme, toggleTheme }) => {
  // State for tracking which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(['health', 'community', 'services', 'support'])
  );

  // Core items (always visible, not in a group)
  const coreItems: MenuItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'progress', label: 'Analytics', icon: BarChart2 },
  ];

  // Grouped menu items
  const menuGroups: MenuGroup[] = [
    {
      id: 'health',
      label: 'Health & Wellness',
      icon: Heart,
      items: [
        { id: 'meal-plans', label: 'Meal Plans', icon: Coffee },
        { id: 'grocery', label: 'Grocery Shop', icon: ShoppingCart },
        { id: 'dining', label: 'Healthy Dining', icon: Utensils },
      ]
    },
    {
      id: 'community',
      label: 'Community & Activities',
      icon: Users,
      items: [
        { id: 'community', label: 'Community', icon: Users },
        { id: 'achievements', label: 'Achievements', icon: Trophy },
        { id: 'facilities', label: 'Facility Locator', icon: MapPin },
      ]
    },
    {
      id: 'services',
      label: 'Services',
      icon: Briefcase,
      items: [
        { id: 'appointments', label: 'Consultations', icon: Calendar },
        { id: 'messages', label: 'Messages', icon: MessageSquare, badge: 3 },
        { id: 'payments', label: 'Payments', icon: CreditCard },
      ]
    },
    {
      id: 'support',
      label: 'Support & Settings',
      icon: Headphones,
      items: [
        { id: 'support', label: 'Help & Support', icon: HelpCircle },
        { id: 'admin', label: 'Admin Panel', icon: Shield },
      ]
    }
  ];

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Render a menu item
  const renderMenuItem = (item: MenuItem, isInGroup: boolean = false) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    return (
      <button
        key={item.id}
        onClick={() => setActiveTab(item.id)}
        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group relative overflow-hidden ${
          isInGroup ? 'pl-11' : 'pl-4'
        } ${
          isActive 
            ? 'text-health-800 dark:text-health-400 bg-white dark:bg-stone-800 shadow-sm border border-stone-100 dark:border-stone-700' 
            : 'text-gray-500 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-stone-800/50 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-health-500"></div>}
        <div className="relative">
          <Icon size={16} className={`transition-colors ${isActive ? 'text-health-600 dark:text-health-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`} />
          {item.badge && !isActive && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] flex items-center justify-center rounded-full border border-white dark:border-stone-800">
              {item.badge}
            </span>
          )}
        </div>
        <span className="flex-1 text-left">{item.label}</span>
        {item.badge && isActive && (
          <span className="bg-health-100 dark:bg-health-900/30 text-health-700 dark:text-health-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  // Render a group header
  const renderGroupHeader = (group: MenuGroup) => {
    const GroupIcon = group.icon;
    const isExpanded = expandedGroups.has(group.id);
    const hasActiveItem = group.items.some(item => item.id === activeTab);
    
    return (
      <button
        onClick={() => toggleGroup(group.id)}
        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
          hasActiveItem
            ? 'text-health-700 dark:text-health-400 bg-health-50/50 dark:bg-health-900/10'
            : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white/30 dark:hover:bg-stone-800/30'
        }`}
      >
        <GroupIcon size={14} />
        <span className="flex-1 text-left">{group.label}</span>
        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
    );
  };

  return (
    <div className="w-64 h-screen bg-stone-50/80 dark:bg-stone-900/90 backdrop-blur-xl border-r border-white/20 dark:border-stone-800 flex flex-col py-8 px-4 hidden md:flex z-40 shadow-[1px_0_20px_rgba(0,0,0,0.02)] transition-colors duration-300 overflow-y-auto no-scrollbar">
      <div className="mb-8 px-4 flex items-center gap-3">
         <div className="w-8 h-8 bg-gradient-to-tr from-health-600 to-health-400 rounded-lg shadow-neon flex items-center justify-center text-white font-bold text-lg">W</div>
         <span className="text-xl font-bold tracking-tight text-gray-800 dark:text-white">Well Being</span>
      </div>

      <nav className="flex-1 space-y-1">
        {/* Core items (always visible) */}
        {coreItems.map((item) => renderMenuItem(item, false))}
        
        {/* Separator after core items */}
        <div className="py-2">
          <div className="border-t border-stone-200/50 dark:border-stone-800/50"></div>
        </div>

        {/* Grouped items */}
        {menuGroups.map((group) => {
          const isExpanded = expandedGroups.has(group.id);
          return (
            <div key={group.id} className="space-y-1">
              {renderGroupHeader(group)}
              {isExpanded && (
                <div className="space-y-0.5 py-1">
                  {group.items.map((item) => renderMenuItem(item, true))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="mt-6 px-4 space-y-2 pt-6 border-t border-stone-200 dark:border-stone-800">
        <button 
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm font-medium transition-colors px-4 py-2 hover:bg-white/50 dark:hover:bg-stone-800/50 rounded-lg"
        >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
        </button>
        <button className="w-full flex items-center gap-3 text-gray-400 hover:text-red-600 dark:hover:text-red-400 text-sm font-medium transition-colors px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg">
            <LogOut size={18} />
            Sign Out
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
